import states from '@config/states'
import { geocodeZip, reverseGeocode } from '@lib/geo-api'
import { firstQueryParam } from '@utils/firstQueryParam'
import { convertFromSlug } from '@utils/slugs'

export const PLAY_ZIP_SEARCH_RADIUS_MILES = 15

/** Used by flag-off play facility search (City, ST parsing). */
export function parseCityStateFromSearchText(searchText) {
  const trimmed = String(searchText ?? '').trim()
  const commaIndex = trimmed.indexOf(',')
  if (commaIndex < 0) return null

  const city = trimmed.slice(0, commaIndex).trim()
  const stateRaw = trimmed.slice(commaIndex + 1).trim().split(/\s+/)[0]
  if (!city || !stateRaw) return null

  const stateUpper = stateRaw.toUpperCase()
  if (/^[A-Z]{2}$/.test(stateUpper)) {
    return { city, state: stateUpper }
  }

  const stateEntry = Object.entries(states).find(
    ([, name]) => name.toLowerCase() === stateRaw.toLowerCase(),
  )
  if (stateEntry) {
    return { city, state: stateEntry[0] }
  }

  return null
}

/**
 * Flag-on / golf-facilities search params — prior path (no City/ST parse, no zip radius).
 */
export function getFacilitySearchParamsForPlaySearchQuery(query = {}) {
  const zipTrim = String(firstQueryParam(query.zipCode) ?? '').trim()
  if (zipTrim) return { postalCode: zipTrim, includeAddress: true }

  const searchTrim = String(firstQueryParam(query.searchText) ?? '').trim()
  if (searchTrim) return { search: searchTrim, includeAddress: true }

  const cityRaw = firstQueryParam(query.city)
  const stateRaw = firstQueryParam(query.state)
  const cityTrim = cityRaw ? String(cityRaw).trim() : ''
  const stateTrim = stateRaw ? String(stateRaw).trim() : ''
  if (!cityTrim || !stateTrim) return null
  const city = convertFromSlug(String(cityRaw))
  const state = String(stateRaw).toUpperCase()
  return { city, state, includeAddress: true }
}

function normalizeStateCode(stateRaw) {
  const trimmed = String(stateRaw ?? '').trim()
  if (!trimmed) return null
  const upper = trimmed.toUpperCase()
  if (/^[A-Z]{2}$/.test(upper)) return upper
  const stateEntry = Object.entries(states).find(
    ([, name]) => name.toLowerCase() === trimmed.toLowerCase(),
  )
  return stateEntry?.[0] ?? null
}

/** Flag-off zip fallback: reverse-geocode zip to city/state for direct Algolia city search. */
export async function resolveCityStateSearchParamsFromZipCode(zipCode) {
  const zipTrim = String(zipCode ?? '').trim()
  if (!zipTrim) return null

  const geo = await geocodeZip(zipTrim)
  if (geo?.lat == null || geo?.lng == null) return null

  const reverse = await reverseGeocode(geo)
  const city = reverse?.city ? String(reverse.city).trim() : ''
  const state = normalizeStateCode(reverse?.state)
  if (!city || !state) return null

  return { city, state, includeAddress: true }
}
